import { useRef, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQuery } from '@tanstack/react-query';
import { format, isAfter, isBefore, isValid, parse } from 'date-fns';
import {
  AlertCircleIcon,
  CopyCheckIcon,
  EditIcon,
  ExternalLinkIcon,
  SaveIcon,
  XIcon,
} from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import * as yup from 'yup';

import { FormUnsavedConfirmationWrapper } from '../../../components/pages/backoffice/form-unsaved-confirmation-wrapper.jsx';
import CancelButton from '../../../components/buttons/cancel-button.jsx';
import Error from '../../../components/feedback/error.jsx';
import Loading from '../../../components/feedback/loading.jsx';
import { ImageInputWithPreview } from '../../../components/input-image-with-preview.jsx';
import Heading from '../../../components/typography/heading.jsx';
import BadgeList from '../../../components/ui/badge-list.jsx';
import { Button } from '../../../components/ui/button.jsx';
import { Checkbox } from '../../../components/ui/checkbox.jsx';
import { DateTimeInputWithDatePicker } from '../../../components/ui/date-time-picker.jsx';
import FormCard from '../../../components/ui/form-card.jsx';
import FormFooter from '../../../components/ui/form-footer.jsx';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../components/ui/form.jsx';
import { Input } from '../../../components/ui/input.jsx';
import {
  MultiSelect,
  MultiSelectContent,
  MultiSelectEmpty,
  MultiSelectList,
  MultiSelectSearch,
  MultiSelectTrigger,
  MultiSelectValue,
  renderMultiSelectOptions,
} from '../../../components/ui/multi-select.jsx';
import RichTextEditor from '../../../components/ui/rich-text-editor.jsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select.jsx';
import { Textarea } from '../../../components/ui/textarea.jsx';
import { msCatalystApi } from '../../../services/api/ms-catalyst-api.js';
import { cn } from '../../../utils/cn.js';
import {
  CAMPAIGN_BANNER_DIMENSIONS,
  CAMPAIGN_INACTIVE_HERO_SM_DIMENSIONS,
  CAMPAIGN_INACTIVE_HERO_XL_DIMENSIONS,
  CAMPAIGN_STATES,
  DATETIME_FORMAT,
} from '../../../utils/constants.js';
import { formatDateTimeToISO } from '../../../utils/format.js';
import { REGEX_DATETIME_EU } from '../../../utils/regex.js';

export function CampaignsEdit() {
  const navigate = useNavigate();
  const { campaignId } = useParams();
  const [updateRegulationFile, setUpdateRegulationFile] = useState(false);
  const [initialAdherentShops, setInitialAdherentShops] = useState([]);
  const [initialCampaignState, setInitialCampaignState] = useState(undefined);
  const [multiselectShopsLoading, setMultiselectShopsLoading] = useState(false);
  const [multiselectShopsOptions, setMultiselectShopsOptions] = useState([]);

  const indexRef = useRef(0);

  const formEditCampaign = useForm({
    mode: 'onChange',
    defaultValues: {
      name: '',
      state: null,
      startDate: '',
      closeDate: '',
      endDate: '',
      numOfWinners: 3,
      numOfSubstituteWinners: 3,
      minEntryAmount: 25,
      type: '',
      legalPhrase: '',
      description: '',
      regulationFile: '',
      regulationFilename: '',
      rules: '',
      bannerUrl: '',
      bannerFilename: '',
      heroNotActiveSmFile: '',
      heroNotActiveSmFilename: '',
      heroNotActiveXlFile: '',
      heroNotActiveXlFilename: '',
      adherentShopsVisible: true,
    },
    resolver: yupResolver(
      yup.object().shape({
        name: yup
          .string()
          .min(10, 'Nome da Campanha deve ter pelo menos 10 caracteres'),
        startDate: yup
          .string()
          .required('Data de Início é um campo obrigatório')
          .matches(
            REGEX_DATETIME_EU,
            'Data de Início possui um formato inválido',
          )
          .test('validDate', 'Insira uma data válida', function (value) {
            try {
              const parsedDate = parse(
                value,
                'dd-MM-yyyy HH:mm:ss',
                new Date(),
              );
              const isValidDate = isValid(parsedDate);

              return isValidDate;
            } catch (error) {
              return false;
            }
          })
          .test(
            'isBeforeEndDate',
            'Data de Início deve ser anterior à Data de Conclusão',
            function (value) {
              const { endDate } = this.parent;
              if (!value || !endDate) {
                return true; // If one of the dates is missing, skip this validation
              }

              const parsedStartDate = parse(value, DATETIME_FORMAT, new Date());
              const parsedEndDate = parse(endDate, DATETIME_FORMAT, new Date());

              if (!isValid(parsedStartDate) || !isValid(parsedEndDate)) {
                return true; // Skip this validation if dates are invalid
              }

              // Check if startDate is before to endDate
              return isBefore(parsedStartDate, parsedEndDate);
            },
          ),
        closeDate: yup
          .string()
          .required('Data de Fecho é um campo obrigatório')
          .matches(
            REGEX_DATETIME_EU,
            'Data de Fecho possui um formato inválido',
          )
          .test('validDate', 'Insira uma data válida', function (value) {
            try {
              const parsedDate = parse(
                value,
                'dd-MM-yyyy HH:mm:ss',
                new Date(),
              );
              const isValidDate = isValid(parsedDate);

              return isValidDate;
            } catch (error) {
              return false;
            }
          })
          .test(
            'isAfterStartDate',
            'Data de Fecho deve ser posterior à Data de Início',
            function (value) {
              const { startDate } = this.parent;
              if (!value || !startDate) {
                return true; // If one of the dates is missing, skip this validation
              }

              const parsedCloseDate = parse(
                value,
                'dd-MM-yyyy HH:mm:ss',
                new Date(),
              );
              const parsedStartDate = parse(
                startDate,
                'dd-MM-yyyy HH:mm:ss',
                new Date(),
              );

              if (!isValid(parsedStartDate) || !isValid(parsedCloseDate)) {
                return true; // Skip this validation if dates are invalid
              }

              // Check if endDate is after to startDate
              return isAfter(parsedCloseDate, parsedStartDate);
            },
          ),
        endDate: yup
          .string()
          .required('Data de Conclusão é um campo obrigatório')
          .matches(
            REGEX_DATETIME_EU,
            'Data de Conclusão possui um formato inválido',
          )
          .test('validDate', 'Insira uma data válida', function (value) {
            try {
              const parsedDate = parse(
                value,
                'dd-MM-yyyy HH:mm:ss',
                new Date(),
              );
              const isValidDate = isValid(parsedDate);

              return isValidDate;
            } catch (error) {
              return false;
            }
          })
          .test(
            'isAfterStartDate',
            'Data de Fim deve ser posterior à Data de Fecho',
            function (value) {
              const { closeDate } = this.parent;
              if (!value || !closeDate) {
                return true; // If one of the dates is missing, skip this validation
              }

              const parsedEndDate = parse(
                value,
                'dd-MM-yyyy HH:mm:ss',
                new Date(),
              );
              const parsedCloseDate = parse(
                closeDate,
                'dd-MM-yyyy HH:mm:ss',
                new Date(),
              );

              if (!isValid(parsedCloseDate) || !isValid(parsedEndDate)) {
                return true; // Skip this validation if dates are invalid
              }

              // Check if endDate is after to closeDate
              return isAfter(parsedEndDate, parsedCloseDate);
            },
          ),
        numOfWinners: yup
          .number()
          .positive('Número de Vencedores tem de ser um número positivo')
          .min(1, 'Número de Vencedores tem de ser igual ou maior que 1')
          .required('Número de Vencedores é um campo obrigatório'),
        numOfSubstituteWinners: yup
          .number()
          .positive('Número de Suplentes tem de ser um número positivo')
          .min(1, 'Número de Suplentes tem de ser igual ou maior que 1')
          .required('Número de Suplentes é um campo obrigatório'),
        minEntryAmount: yup
          .number()
          .positive(
            'Valor Minimo para Participação tem de ser um número positivo',
          )
          .min(1, 'Valor Minimo para Participação tem de ser maior que 1')
          .required('Valor Minimo para Participação é um campo obrigatório'),
        description: yup
          .string()
          .max(
            '2000',
            'Condições da Campanha não pode ter mais do 500 caracteres',
          )
          .min('20', 'Condições da Campanha deve ter pelo menos 20 caracteres')
          .required('Condições da Campanha é um campo obrigatório'),
        rules: yup
          .string()
          .max('700', 'Regulamento não pode ter mais do 500 caracteres')
          .min('20', 'Regulamento ter pelo menos 20 caracteres')
          .required('Regulamento é um campo obrigatório'),
      }),
    ),
  });

  const queryCampaign = useQuery({
    queryKey: ['campaign', campaignId],
    queryFn: async () => {
      const { data: getCampaignData } = await msCatalystApi.get(
        `/campaigns/${campaignId}`,
      );
      const campaignData = getCampaignData.data;

      setInitialCampaignState(campaignData.campaign.state);
      setInitialAdherentShops(
        campaignData.shops.map((item) => ({
          status: item.status,
          label: item.name,
          id: item.ROWID,
          campaignsShopsId: item.campaignsShopsId,
        })),
      );

      delete campaignData.campaign.CREATORID;
      delete campaignData.campaign.CREATEDTIME;
      delete campaignData.campaign.MODIFIEDTIME;

      formEditCampaign.reset({
        ...campaignData.campaign,
        startDate: format(
          campaignData.campaign.startDate,
          'dd-MM-yyyy HH:mm:ss',
        ),
        endDate: format(campaignData.campaign.endDate, 'dd-MM-yyyy HH:mm:ss'),
        closeDate: format(
          campaignData.campaign.closeDate,
          'dd-MM-yyyy HH:mm:ss',
        ),
        adherentShops: campaignData.shops.map((item) => item.ROWID),
        bannerUrl: campaignData.campaign.bannerUrl,
      });

      return campaignData;
    },
  });

  const queryShops = useQuery({
    queryKey: ['shops'],
    queryFn: async () => {
      const { data: shops } = await msCatalystApi.get(`shops/all?status=true`);

      const shopsData = shops.data.map((item) => ({
        label: item.name,
        value: item.ROWID,
      }));

      setMultiselectShopsOptions(shopsData);

      return shopsData;
    },
    enabled:
      !!queryCampaign.isSuccess &&
      queryCampaign.data?.campaign.state !== CAMPAIGN_STATES.CONCLUDED &&
      queryCampaign.data?.campaign.state !== CAMPAIGN_STATES.READY_TO_DRAW,
  });

  const mutationUpdateCampaign = useMutation({
    mutationFn: async (data) => {
      const formData = new FormData();
      formData.append('bannerFile', data.bannerFile);
      formData.append('regulationFile', data.regulationFile);

      if (data?.heroNotActiveSmFile) {
        formData.append('heroNotActiveSmFile', data.heroNotActiveSmFile);
      }

      if (data?.heroNotActiveXlFile) {
        formData.append('heroNotActiveXlFile', data.heroNotActiveXlFile);
      }

      const newData = { ...data };

      delete newData.bannerFile;
      delete newData.regulationFile;
      delete newData.heroNotActiveSmFile;
      delete newData.heroNotActiveXlFile;

      newData.startDate = formatDateTimeToISO(data.startDate);
      newData.closeDate = formatDateTimeToISO(data.closeDate);
      newData.endDate = formatDateTimeToISO(data.endDate);

      formData.append('data', JSON.stringify(newData));

      const { data: updateCampaignRes } = await msCatalystApi.put(
        `/campaigns/${campaignId}`,
        formData,
      );

      return updateCampaignRes.data;
    },
    onSuccess: (data) => {
      toast.success('Campanha editada com successo!');
    },
    onError: (err) => {
      if (err?.response?.data) {
        toast.error(`Erro ao editar campanha: ${err.data?.message}`);
      } else {
        toast.error('Erro ao editar campanha');
      }
    },
  });

  const mutationCreateCampaignAdherentShops = useMutation({
    mutationFn: async (data) => {
      return await msCatalystApi.post(`/campaigns/adherent-shops`, data);
    },
    onError: (err) => {
      toast.error('Erro ao adicionar loja aderente');
    },
  });

  const mutationDeleteCampaignAdherentShops = useMutation({
    mutationFn: async ({ ids }) => {
      return await msCatalystApi.delete(`/campaigns/adherent-shops?ids=${ids}`);
    },
    onError: (err) => {
      toast.error('Erro ao remover loja aderente');
    },
  });

  const searchShops = async (items, keyword) => {
    if (!keyword) {
      setMultiselectShopsOptions(
        queryShops?.data.map((item) => ({
          label: item.name,
          value: item.ROWID,
        })),
      );
    }

    const lowerKeyword = keyword.toLowerCase();
    const filtered = items.filter((item) =>
      item.label.toLowerCase().includes(lowerKeyword),
    );

    if (!filtered.length) {
      return [];
    }

    return filtered;
  };

  const handleSearchShops = async (items, keyword) => {
    const index = ++indexRef.current;
    setMultiselectShopsLoading(true);
    const newOptions = await searchShops(items, keyword);
    if (indexRef.current === index) {
      setMultiselectShopsOptions(newOptions);
      setMultiselectShopsLoading(false);
    }
  };

  const onFormSubmit = async (data) => {
    console.log('onFormSubmit', data);

    const adherentShops = data.adherentShops;
    delete data.adherentShops;

    data.minEntryAmount = `${data.minEntryAmount}`;

    await mutationUpdateCampaign.mutateAsync(data);

    if (
      queryCampaign.data?.campaign?.state === CAMPAIGN_STATES.YET_TO_START ||
      queryCampaign.data?.campaign?.state === CAMPAIGN_STATES.ONGOING
    ) {
      // Determine the changes in adherent shops
      const addedShops = adherentShops.filter(
        (shopId) => !initialAdherentShops.some((shop) => shop.id === shopId),
      );

      const removedShops = initialAdherentShops.filter(
        (shop) => !adherentShops.includes(shop.id),
      );

      // Add new adherent shops
      if (addedShops.length) {
        await mutationCreateCampaignAdherentShops.mutateAsync(
          addedShops.map((item) => ({
            shopId: item,
            campaignId,
          })),
        );
      }

      // Remove shops that were deselected
      if (removedShops.length) {
        await mutationDeleteCampaignAdherentShops.mutateAsync({
          ids: removedShops.map((item) => item.campaignsShopsId),
        });
      }
    }

    navigate(`/backoffice/campaigns/${campaignId}`);
  };

  if (queryCampaign.isLoading) {
    return <Loading />;
  }

  if (queryCampaign.isError) {
    return <Error />;
  }

  return (
    <FormUnsavedConfirmationWrapper form={formEditCampaign}>
      <div className="pb-6">
        <Heading className="pb-2">Editar Campanha</Heading>
        <h2 className="text-lg text-zinc-600 dark:text-zinc-400">
          {queryCampaign.data?.campaign?.name}
        </h2>
      </div>

      <Form {...formEditCampaign}>
        <form onSubmit={formEditCampaign.handleSubmit(onFormSubmit)}>
          <div className="flex flex-col space-y-10">
            <div className="flex flex-col gap-5">
              <FormCard>
                <FormField
                  control={formEditCampaign.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem className="col-span-1 lg:col-span-2">
                      <FormLabel required={true}>Nome</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>

                      <FormDescription>
                        O nome da campanha tem de ser único
                      </FormDescription>

                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={formEditCampaign.control}
                  name="state"
                  render={({ field }) => (
                    <FormItem className="col-span-1 lg:col-span-2">
                      <FormLabel required={true}>Estado</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {Object.keys(CAMPAIGN_STATES).map((key, i) => {
                            return (
                              <SelectItem key={i} value={CAMPAIGN_STATES[key]}>
                                {CAMPAIGN_STATES[key]}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={formEditCampaign.control}
                  name="startDate"
                  render={({ field }) => {
                    let selectedDate = null;

                    if (field?.value?.match(REGEX_DATETIME_EU)) {
                      const [dateStr, hoursStr] = field.value.split(' ');
                      const [day, month, year] = dateStr.split('-');
                      const [hours, minutes, seconds] = hoursStr.split(':');

                      selectedDate = new Date(
                        `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`,
                      );
                    }

                    return (
                      <FormItem>
                        <FormLabel>Data de Início</FormLabel>
                        <FormControl>
                          <DateTimeInputWithDatePicker
                            input={{
                              ...field,
                              disabled:
                                initialCampaignState ===
                                CAMPAIGN_STATES.YET_TO_START
                                  ? false
                                  : true,
                            }}
                            calendar={{
                              selected: selectedDate,
                              onSelect: (val) => {
                                field.onChange(
                                  format(new Date(val), 'dd-MM-yyyy HH:mm'),
                                );
                              },
                            }}
                          />
                        </FormControl>

                        <FormDescription>
                          Data de Início tem de seguir o formato:{' '}
                          <i>dd-MM-yyyy HH:mm:ss</i>
                        </FormDescription>

                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />

                <FormField
                  control={formEditCampaign.control}
                  name="closeDate"
                  render={({ field }) => {
                    let selectedDate = null;

                    if (field?.value?.match(REGEX_DATETIME_EU)) {
                      const [dateStr, hoursStr] = field.value.split(' ');
                      const [day, month, year] = dateStr.split('-');
                      const [hours, minutes, seconds] = hoursStr.split(':');

                      selectedDate = new Date(
                        `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`,
                      );
                    }

                    return (
                      <FormItem>
                        <FormLabel className="text-left">
                          Data de Fecho de Participações
                        </FormLabel>
                        <FormControl>
                          <DateTimeInputWithDatePicker
                            input={{
                              ...field,
                              disabled:
                                initialCampaignState ===
                                  CAMPAIGN_STATES.YET_TO_START ||
                                initialCampaignState === CAMPAIGN_STATES.ONGOING
                                  ? false
                                  : true,
                            }}
                            calendar={{
                              selected: selectedDate,
                              onSelect: (val) => {
                                field.onChange(
                                  format(new Date(val), 'dd-MM-yyyy HH:mm:ss'),
                                );
                              },
                            }}
                          />
                        </FormControl>

                        <FormDescription>
                          Data de Fecho de Participações tem de seguir o
                          formato: <i>dd-MM-yyyy HH:mm:ss</i>
                        </FormDescription>

                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />

                <FormField
                  control={formEditCampaign.control}
                  name="endDate"
                  render={({ field }) => {
                    let selectedDate = null;

                    if (field?.value?.match(REGEX_DATETIME_EU)) {
                      const [dateStr, hoursStr] = field.value.split(' ');
                      const [day, month, year] = dateStr.split('-');
                      const [hours, minutes, seconds] = hoursStr.split(':');

                      selectedDate = new Date(
                        `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`,
                      );
                    }

                    return (
                      <FormItem>
                        <FormLabel className="text-left">Data de Fim</FormLabel>
                        <FormControl>
                          <DateTimeInputWithDatePicker
                            input={{
                              ...field,
                              disabled:
                                initialCampaignState ===
                                  CAMPAIGN_STATES.YET_TO_START ||
                                initialCampaignState === CAMPAIGN_STATES.ONGOING
                                  ? false
                                  : true,
                            }}
                            calendar={{
                              selected: selectedDate,
                              onSelect: (val) => {
                                field.onChange(
                                  format(new Date(val), 'dd-MM-yyyy HH:mm:ss'),
                                );
                              },
                            }}
                          />
                        </FormControl>

                        <FormDescription>
                          Data de Fim tem de seguir o formato:{' '}
                          <i>dd-MM-yyyy HH:mm:ss</i>
                        </FormDescription>

                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />

                <FormField
                  control={formEditCampaign.control}
                  name="numOfWinners"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nº de Vencedores</FormLabel>
                      <FormControl>
                        <Input
                          disabled={
                            initialCampaignState ===
                              CAMPAIGN_STATES.YET_TO_START ||
                            initialCampaignState === CAMPAIGN_STATES.ONGOING
                              ? false
                              : true
                          }
                          step="1"
                          type="number"
                          {...field}
                        />
                      </FormControl>

                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={formEditCampaign.control}
                  name="numOfSubstituteWinners"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nº de Suplentes</FormLabel>
                      <FormControl>
                        <Input
                          disabled={
                            initialCampaignState ===
                              CAMPAIGN_STATES.YET_TO_START ||
                            initialCampaignState === CAMPAIGN_STATES.ONGOING
                              ? false
                              : true
                          }
                          step="1"
                          type="number"
                          {...field}
                        />
                      </FormControl>

                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={formEditCampaign.control}
                  name="minEntryAmount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Valor Minimo de Participação (€)</FormLabel>
                      <FormControl>
                        <Input
                          disabled={
                            initialCampaignState ===
                            CAMPAIGN_STATES.YET_TO_START
                              ? false
                              : true
                          }
                          step="0.01"
                          type="number"
                          {...field}
                        />
                      </FormControl>

                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={formEditCampaign.control}
                  name="legalPhrase"
                  render={({ field }) => (
                    <FormItem className="col-span-1 lg:col-span-2">
                      <FormLabel required={true}>Frase Legal</FormLabel>

                      <FormControl>
                        <Textarea
                          placeholder="Frase legal da campanha"
                          {...field}
                        />
                      </FormControl>

                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={formEditCampaign.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem className="col-span-1 lg:col-span-2">
                      <FormLabel required={true}>
                        Condições da Campanha
                      </FormLabel>
                      <FormControl>
                        <RichTextEditor
                          value={field.value}
                          onChange={field.onChange}
                        />
                      </FormControl>

                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={formEditCampaign.control}
                  name="regulationFile"
                  render={({ field }) => {
                    return updateRegulationFile ? (
                      <FormItem className="col-span-1 md:col-span-2">
                        <FormLabel>Regulamento (Ficheiro)</FormLabel>

                        <FormControl>
                          <Input
                            type="file"
                            onChange={(ev) => {
                              const files = ev.target.files;
                              if (!files?.length) return;
                              const fileName = files[0].name;
                              formEditCampaign.setValue(
                                'regulationFile',
                                files[0],
                              );
                              formEditCampaign.setValue(
                                'regulationFilename',
                                fileName,
                                {
                                  shouldDirty: true,
                                },
                              );
                            }}
                          />
                        </FormControl>

                        <FormMessage />

                        <Button
                          className="mt-6"
                          variant="destructive"
                          onClick={() =>
                            setUpdateRegulationFile(!updateRegulationFile)
                          }
                        >
                          <XIcon />
                          Cancelar Edição
                        </Button>
                      </FormItem>
                    ) : (
                      <FormItem className="col-span-1 md:col-span-2">
                        <FormLabel>Regulamento (Ficheiro)</FormLabel>

                        <div className="ring-offset-backgroundfocus-visible:outline-none flex h-auto items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
                          {formEditCampaign.getValues('regulationFilename')}

                          <div>
                            <a
                              href={
                                queryCampaign?.data?.campaign?.regulationUrl
                              }
                              target="_blank"
                              rel="noreferrer noopener"
                            >
                              <Button
                                type="button"
                                className="h-auto p-2 text-xs"
                                variant="ghost"
                              >
                                <ExternalLinkIcon className="size-4" />
                                Ver PDF
                              </Button>
                            </a>

                            <Button
                              className="h-auto p-2 text-xs"
                              variant="ghost"
                              onClick={() =>
                                setUpdateRegulationFile(!updateRegulationFile)
                              }
                            >
                              <EditIcon />
                              Editar
                            </Button>
                          </div>
                        </div>

                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />

                <FormField
                  control={formEditCampaign.control}
                  name="rules"
                  render={({ field }) => (
                    <FormItem className="col-span-1 lg:col-span-2">
                      <FormLabel required={true}>
                        Regulamento (Descrição)
                      </FormLabel>
                      <FormControl>
                        <RichTextEditor
                          value={field.value}
                          onChange={field.onChange}
                        />
                      </FormControl>

                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="col-span-1 lg:col-span-2">
                  <p
                    className={cn(
                      'mb-3 inline-block text-sm font-medium leading-none',
                      initialCampaignState !== CAMPAIGN_STATES.CONCLUDED &&
                        'hidden',
                    )}
                  >
                    Lojas Aderentes
                  </p>
                  {initialCampaignState === CAMPAIGN_STATES.CONCLUDED ||
                  initialCampaignState === CAMPAIGN_STATES.READY_TO_DRAW ? (
                    <BadgeList
                      items={queryCampaign.data?.shops.map((item) => ({
                        status: item.status,
                        label: item.name,
                        value: item.ROWID,
                      }))}
                      status={{
                        key: 'status',
                        colors: { true: 'bg-green-400', false: 'bg-red-400' },
                      }}
                    />
                  ) : (
                    <FormField
                      control={formEditCampaign.control}
                      name="adherentShops"
                      render={({ field }) => (
                        <FormItem className="col-span-1 lg:col-span-2">
                          <FormLabel required={true}>Lojas Aderentes</FormLabel>
                          <FormControl className="flex flex-row gap-x-2">
                            {queryShops.isLoading ? (
                              <div className="flex flex-row items-center gap-2">
                                <div
                                  className="text-surface inline-block size-8 animate-spin rounded-full border-4 border-solid border-current border-e-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite] dark:text-white"
                                  role="status"
                                />
                                <span>A carregar lojas...</span>
                              </div>
                            ) : queryShops.isError ? (
                              <div className="flex flex-row items-center gap-2">
                                <AlertCircleIcon className="size-5" />
                                <span>
                                  Ocorreu um erro ao carregar as lojas...
                                </span>
                              </div>
                            ) : multiselectShopsOptions.length === 0 ? (
                              <div className="flex flex-row items-center">
                                <div
                                  className="text-surface inline-block size-8 animate-spin rounded-full border-4 border-solid border-current border-e-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite] dark:text-white"
                                  role="status"
                                />
                                <span className="mt-4">
                                  A carregar lojas...
                                </span>
                              </div>
                            ) : (
                              <div className="flex flex-col items-start gap-2 xl:flex-row xl:items-center">
                                <MultiSelect
                                  value={field.value}
                                  onSearch={(val) => {
                                    handleSearchShops(queryShops.data, val);
                                  }}
                                  onValueChange={(values) => {
                                    field.onChange(values);
                                  }}
                                >
                                  <MultiSelectTrigger>
                                    <MultiSelectValue maxDisplay={20} />
                                  </MultiSelectTrigger>
                                  <MultiSelectContent>
                                    <MultiSelectList>
                                      <MultiSelectSearch />
                                      {renderMultiSelectOptions(
                                        multiselectShopsOptions,
                                      )}
                                      <MultiSelectEmpty>
                                        {!multiselectShopsOptions.length ? (
                                          'Não foram encontrados resultados'
                                        ) : queryShops.isLoading ||
                                          multiselectShopsLoading ||
                                          (queryShops.status === 'pending' &&
                                            queryShops.isPending) ? (
                                          <Loading />
                                        ) : (
                                          'Não foram encontrados resultados'
                                        )}
                                      </MultiSelectEmpty>
                                    </MultiSelectList>
                                  </MultiSelectContent>
                                </MultiSelect>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  onClick={() => {
                                    formEditCampaign.setValue(
                                      'adherentShops',
                                      queryShops.data.map((item) => item.value),
                                    );
                                  }}
                                >
                                  <CopyCheckIcon className="mr-1.5 size-4" />
                                  Selecionar todas as Lojas
                                </Button>
                              </div>
                            )}
                          </FormControl>

                          <FormDescription>
                            As lojas cujas faturas serão válidas para gerar
                            cupões de participação na campanha
                          </FormDescription>

                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}
                </div>

                <FormField
                  control={formEditCampaign.control}
                  name="adherentShopsVisible"
                  render={({ field }) => (
                    <FormItem className="col-span-1 lg:col-span-2">
                      <div className="flex flex-row items-center space-x-2">
                        <FormControl>
                          <Checkbox
                            className="mr-1 size-5"
                            checked={field.value}
                            onCheckedChange={(checked) =>
                              field.onChange(checked)
                            }
                          />
                        </FormControl>
                        <FormLabel className="ml-4 space-y-0 font-normal">
                          Lojas Aderentes visivéis no Portal do Cliente?
                        </FormLabel>
                      </div>

                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={formEditCampaign.control}
                  name="showOrderedWinners"
                  render={({ field }) => (
                    <FormItem className="col-span-1 lg:col-span-2">
                      <div className="flex flex-row items-center space-x-2">
                        <FormControl>
                          <Checkbox
                            className="mr-1 size-5"
                            checked={field.value}
                            onCheckedChange={(checked) =>
                              field.onChange(checked)
                            }
                          />
                        </FormControl>
                        <FormLabel className="ml-4 space-y-0 font-normal">
                          Vencedores da Campanha serão apresentados por ordem
                          sequencial? (1º, 2º, 3º)
                        </FormLabel>
                      </div>

                      <FormMessage />
                    </FormItem>
                  )}
                />
              </FormCard>
            </div>

            <div className="flex flex-col gap-5">
              <h2 className="text-xl font-medium">Página da Campanha</h2>

              <FormCard>
                <FormField
                  control={formEditCampaign.control}
                  name="link"
                  render={({ field }) => (
                    <FormItem className="col-span-1 lg:col-span-2">
                      <FormLabel>Link</FormLabel>
                      <FormControl>
                        <Input disabled={true} {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={formEditCampaign.control}
                  name="bannerFile"
                  render={({ field }) => (
                    <ImageInputWithPreview
                      label="Banner Microsite"
                      dimensions={CAMPAIGN_BANNER_DIMENSIONS}
                      fileFieldName="bannerFile"
                      filenameFieldName="bannerFilename"
                      currentFilename={formEditCampaign.getValues(
                        'bannerFilename',
                      )}
                      imageUrl={queryCampaign.data.campaign.bannerUrl}
                      onFileChange={(file, filename) => {
                        formEditCampaign.setValue('bannerFile', file);
                        formEditCampaign.setValue('bannerFilename', filename, {
                          shouldDirty: true,
                        });
                      }}
                    />
                  )}
                />

                <FormField
                  control={formEditCampaign.control}
                  name="heroNotActiveSmFile"
                  render={({ field }) => (
                    <ImageInputWithPreview
                      label="Imagem de Campanha Inativa - Ecrãs Pequenos"
                      dimensions={CAMPAIGN_INACTIVE_HERO_SM_DIMENSIONS}
                      fileFieldName="heroNotActiveSmFile"
                      filenameFieldName="heroNotActiveSmFilename"
                      currentFilename={formEditCampaign.getValues(
                        'heroNotActiveSmFilename',
                      )}
                      imageUrl={queryCampaign.data.campaign.heroNotActiveSmUrl}
                      onFileChange={(file, filename) => {
                        formEditCampaign.setValue('heroNotActiveSmFile', file);
                        formEditCampaign.setValue(
                          'heroNotActiveSmFilename',
                          filename,
                          {
                            shouldDirty: true,
                          },
                        );
                      }}
                    />
                  )}
                />

                <FormField
                  control={formEditCampaign.control}
                  name="heroNotActiveXlFile"
                  render={({ field }) => (
                    <ImageInputWithPreview
                      label="Imagem de Campanha Inativa - Ecrãs Grandes"
                      dimensions={CAMPAIGN_INACTIVE_HERO_XL_DIMENSIONS}
                      fileFieldName="heroNotActiveXlFile"
                      filenameFieldName="heroNotActiveXlFilename"
                      currentFilename={formEditCampaign.getValues(
                        'heroNotActiveXlFilename',
                      )}
                      imageUrl={queryCampaign.data.campaign.heroNotActiveXlUrl}
                      onFileChange={(file, filename) => {
                        formEditCampaign.setValue('heroNotActiveXlFile', file);
                        formEditCampaign.setValue(
                          'heroNotActiveXlFilename',
                          filename,
                          {
                            shouldDirty: true,
                          },
                        );
                      }}
                    />
                  )}
                />
              </FormCard>
            </div>
          </div>

          <FormFooter>
            <CancelButton />
            <Button
              type="submit"
              disabled={
                !formEditCampaign.formState.isDirty ||
                formEditCampaign.formState.isSubmitting ||
                mutationUpdateCampaign.isPending
              }
              isLoading={
                formEditCampaign.formState.isSubmitting ||
                mutationUpdateCampaign.isPending
              }
            >
              <SaveIcon />
              Guardar Alterações
            </Button>
          </FormFooter>
        </form>
      </Form>
    </FormUnsavedConfirmationWrapper>
  );
}
